import { useMemo } from 'react';
import { useProfile } from './useProfiles';

/**
 * Hook to determine if the current user has any direct reports based on the org structure.
 * This reads from the denormalized directReportsCount field on the Profile, which is
 * automatically maintained by the backend when relationships are created/updated/deleted.
 *
 * @param crewSlug - The crew slug
 * @param profileId - The profile ID or displayName to check
 * @returns Object containing:
 *   - hasDirectReports: boolean indicating if user has any direct reports
 *   - directReportsCount: number of direct reports
 *   - isLoading: loading state
 */
export const useHasDirectReports = (crewSlug: string, profileHandle: string) => {
    const { data: profileResponse, isLoading } = useProfile(crewSlug, profileHandle);

    const result = useMemo(() => {
        const count = profileResponse?.data?.directReportsCount || 0;
        return {
            hasDirectReports: count > 0,
            directReportsCount: count
        };
    }, [profileResponse]);

    return {
        ...result,
        isLoading
    };
};
